/* eslint-env jest */
import shouldShowFreeMagnets, {
  afterBuildFreeMagnetsRoute, magnetStopDestination, readyToDecide, shouldShowFreeItem,
} from './freeMagnetsVisibility';

const magnets = { couponType: 'FREE_MAGNETS_WITH_FREE_SHIPPING', value: 0 };
const pct = { couponType: 'PERCENTAGE_WITH_FREE_SHIPPING', value: 20 };

describe('shouldShowFreeMagnets (issue #5218 item 8)', () => {
  it('shows when the magnet tier is live and the fresh status qualifies', () => {
    expect(shouldShowFreeMagnets({
      offer: magnets, qualifies: true, hasFreeMagnets: false, allJpg: false,
    })).toBe(true);
  });

  it('hides for every other offer, and for no offer', () => {
    expect(shouldShowFreeMagnets({
      offer: pct, qualifies: true, hasFreeMagnets: false, allJpg: false,
    })).toBe(false);
    expect(shouldShowFreeMagnets({
      offer: null, qualifies: true, hasFreeMagnets: false, allJpg: false,
    })).toBe(false);
  });

  it('requires an EXPLICIT true qualifies -- undefined/false/"true" hide', () => {
    expect(shouldShowFreeMagnets({
      offer: magnets, qualifies: undefined, hasFreeMagnets: false, allJpg: false,
    })).toBe(false);
    expect(shouldShowFreeMagnets({
      offer: magnets, qualifies: false, hasFreeMagnets: false, allJpg: false,
    })).toBe(false);
    expect(shouldShowFreeMagnets({
      offer: magnets, qualifies: 'true', hasFreeMagnets: false, allJpg: false,
    })).toBe(false);
  });

  it('hides once the cart already carries the free sheet', () => {
    expect(shouldShowFreeMagnets({
      offer: magnets, qualifies: true, hasFreeMagnets: true, allJpg: false,
    })).toBe(false);
  });

  it('never shows for an all-JPG session', () => {
    expect(shouldShowFreeMagnets({
      offer: magnets, qualifies: true, hasFreeMagnets: false, allJpg: true,
    })).toBe(false);
  });
});

describe('afterBuildFreeMagnetsRoute', () => {
  it("routes a family's FIRST package to the magnet page from the fresh post-build status", () => {
    // The cached cart said qualifies=false before the build; the build response
    // says true. Only the fresh status is consulted.
    const route = afterBuildFreeMagnetsRoute({
      next: '/confirm/77',
      orderPackageId: 77,
      offer: magnets,
      status: { qualifies: true, hasFreeMagnets: false },
      allJpg: false,
    });
    // Audience-neutral path since seniors reach the same page (was
    // /elementary-multi-pose/free-magnets/77).
    expect(route).toBe('/free-magnets/77?next=%2Fconfirm%2F77');
  });

  // The senior flow asks the SAME gate with the SAME shape -- nothing about the
  // audience reaches this function, and a senior offer that grants magnets must
  // be answered exactly like an elementary one. Pinned because the senior
  // builder's `next` is the add-ons chain rather than /confirm, and a helper
  // that quietly assumed the elementary destination is how this would regress.
  it('routes a senior build to the same page, carrying the senior add-ons destination', () => {
    const route = afterBuildFreeMagnetsRoute({
      next: '/seniors/addons/512/add',
      orderPackageId: 512,
      offer: magnets,
      status: { qualifies: true, hasFreeMagnets: false },
      allJpg: false,
    });
    expect(route).toBe('/free-magnets/512?next=%2Fseniors%2Faddons%2F512%2Fadd');
  });

  it('goes straight on when the gate says no, with no status, or with no order package', () => {
    const base = {
      next: '/confirm/77', orderPackageId: 77, offer: magnets, allJpg: false,
    };
    expect(afterBuildFreeMagnetsRoute({ ...base, offer: pct, status: { qualifies: true } })).toBe('/confirm/77');
    expect(afterBuildFreeMagnetsRoute({ ...base, status: undefined })).toBe('/confirm/77');
    expect(afterBuildFreeMagnetsRoute({
      ...base, status: { qualifies: true, hasFreeMagnets: true },
    })).toBe('/confirm/77');
    expect(afterBuildFreeMagnetsRoute({
      ...base, orderPackageId: null, status: { qualifies: true },
    })).toBe('/confirm/77');
  });
});

// The senior builder's post-build funnel. Every senior path (built package,
// All-Digital fast path, Buy All continue/just-added) goes through this, so its
// three rules are pinned here rather than in a component test the repo has no
// framework for.
describe('magnetStopDestination', () => {
  const base = {
    next: '/seniors/addons/512/add', orderPackageId: 512, allJpg: false, cartId: 9,
  };

  it('goes straight on WITHOUT reading the cart when the offer grants no magnets', async () => {
    // The common case, and the read would only be discarded by the gate.
    const readStatus = jest.fn();
    expect(await magnetStopDestination({ ...base, offer: pct, readStatus }))
      .toBe('/seniors/addons/512/add');
    expect(readStatus).not.toHaveBeenCalled();
  });

  it('stops at the magnet page when the fresh flags qualify', async () => {
    const readStatus = jest.fn().mockResolvedValue({ qualifies: true, hasFreeMagnets: false });
    expect(await magnetStopDestination({ ...base, offer: magnets, readStatus }))
      .toBe('/free-magnets/512?next=%2Fseniors%2Faddons%2F512%2Fadd');
    expect(readStatus).toHaveBeenCalledWith({ cartId: 9 });
  });

  it('continues to next when the status read throws, and reports it', async () => {
    // The package is already in the cart here: a rejection must not strand the
    // customer on a spinner with no way forward.
    const readStatus = jest.fn().mockRejectedValue(new Error('cart 404'));
    const onStatusError = jest.fn();
    expect(await magnetStopDestination({
      ...base, offer: magnets, readStatus, onStatusError,
    })).toBe('/seniors/addons/512/add');
    expect(onStatusError).toHaveBeenCalled();
  });

  it('honours the gate when the fresh flags do not qualify', async () => {
    const readStatus = jest.fn().mockResolvedValue({ qualifies: false, hasFreeMagnets: false });
    expect(await magnetStopDestination({ ...base, offer: magnets, readStatus }))
      .toBe('/seniors/addons/512/add');
  });
});

// The decision may only be taken on REAL pose data. usePoses serves
// placeholderData [] while fetching; deciding on it skipped the page for every
// customer (Timber Point canary, 2026-09-01). readyToDecide is the pinned contract.
describe('readyToDecide', () => {
  const base = {
    loadingPackage: false,
    packageId: 877,
    loadingOffer: false,
    loadingCart: false,
    poses: [{ id: 1 }],
    posesPlaceholder: false,
  };
  it('is ready on settled queries with real pose data', () => {
    expect(readyToDecide(base)).toBe(true);
  });
  it('is NOT ready while the poses query is still on its placeholder', () => {
    expect(readyToDecide({ ...base, poses: [], posesPlaceholder: true })).toBe(false);
  });
  it('is ready on genuinely empty real data (the allJpg skip is then legitimate)', () => {
    expect(readyToDecide({ ...base, poses: [] })).toBe(true);
  });
  it('is NOT ready before the order package resolves a packageId', () => {
    expect(readyToDecide({ ...base, packageId: undefined })).toBe(false);
  });
  it('is NOT ready while the gifts catalog or backgrounds are on their placeholder', () => {
    expect(readyToDecide({ ...base, addonsPlaceholder: true })).toBe(false);
    expect(readyToDecide({ ...base, backgroundsPlaceholder: true })).toBe(false);
  });
  it('is NOT ready while any query is loading', () => {
    expect(readyToDecide({ ...base, loadingOffer: true })).toBe(false);
    expect(readyToDecide({ ...base, loadingCart: true })).toBe(false);
    expect(readyToDecide({ ...base, loadingPackage: true })).toBe(false);
  });
});

// Free item of your choice (#5747): the same gate, per PACKAGE.
describe('shouldShowFreeItem / afterBuildFreeMagnetsRoute for a free-item offer', () => {
  const cutouts = [
    { id: 160, name: 'Weekly Dry Erase Magnet - School', allowsJpgPoses: false },
    { id: 161, name: 'Weekly Dry Erase Magnet - Groovy', allowsJpgPoses: false },
  ];
  const freeItem = { couponType: 'FREE_ADDON_CHOICE', value: 0, freeAddons: cutouts };
  const packages = [
    { order_package_id: 41, addon_id: null, addon_sheet_id: null },
    { order_package_id: 42, addon_id: 160, addon_sheet_id: 9 },
  ];

  it('shows for a qualifying package with no pick yet', () => {
    expect(shouldShowFreeItem({
      offer: freeItem, orderPackageId: 41, freeItemPackages: packages, allJpg: false,
    })).toBe(true);
    expect(shouldShowFreeItem({
      offer: freeItem, orderPackageId: '41', freeItemPackages: packages, allJpg: false,
    })).toBe(true);
  });

  it('hides for a package that already carries its pick, one the offer does not cover, or no list', () => {
    expect(shouldShowFreeItem({
      offer: freeItem, orderPackageId: 42, freeItemPackages: packages, allJpg: false,
    })).toBe(false);
    expect(shouldShowFreeItem({
      offer: freeItem, orderPackageId: 43, freeItemPackages: packages, allJpg: false,
    })).toBe(false);
    expect(shouldShowFreeItem({
      offer: freeItem, orderPackageId: 41, freeItemPackages: undefined, allJpg: false,
    })).toBe(false);
  });

  it('hides for every other offer, and for a set that is empty', () => {
    expect(shouldShowFreeItem({
      offer: magnets, orderPackageId: 41, freeItemPackages: packages, allJpg: false,
    })).toBe(false);
    expect(shouldShowFreeItem({
      offer: { ...freeItem, freeAddons: [] }, orderPackageId: 41, freeItemPackages: packages, allJpg: false,
    })).toBe(false);
  });

  it('hides an all-JPG session only when EVERY design refuses JPGs', () => {
    expect(shouldShowFreeItem({
      offer: freeItem, orderPackageId: 41, freeItemPackages: packages, allJpg: true,
    })).toBe(false);
    const withFramed = { ...freeItem, freeAddons: [...cutouts, { id: 124, name: '2027 Calendar Magnet', allowsJpgPoses: null }] };
    expect(shouldShowFreeItem({
      offer: withFramed, orderPackageId: 41, freeItemPackages: packages, allJpg: true,
    })).toBe(true);
  });

  it('routes the build to the free-item page from the fresh per-package status, carrying next', () => {
    const status = { qualifies: false, hasFreeMagnets: false, freeItemPackages: packages };
    expect(afterBuildFreeMagnetsRoute({
      next: '/c/gifts/builder?o=41', orderPackageId: 41, offer: freeItem, status, allJpg: false,
    })).toBe('/free-item/41?next=%2Fc%2Fgifts%2Fbuilder%3Fo%3D41');
    // The second child's package, already picked for: straight on.
    expect(afterBuildFreeMagnetsRoute({
      next: '/c/gifts/builder?o=42', orderPackageId: 42, offer: freeItem, status, allJpg: false,
    })).toBe('/c/gifts/builder?o=42');
  });

  it('magnetStopDestination reads the cart for a free-item offer and stops at its page', async () => {
    const readStatus = jest.fn().mockResolvedValue({ qualifies: false, hasFreeMagnets: false, freeItemPackages: packages });
    expect(await magnetStopDestination({
      next: '/c/seniors/addons/41', orderPackageId: 41, offer: freeItem, allJpg: false, cartId: 7, readStatus,
    })).toBe('/free-item/41?next=%2Fc%2Fseniors%2Faddons%2F41');
    expect(readStatus).toHaveBeenCalledWith({ cartId: 7 });
  });
});
